/** @jsx jsx **/
import { jsx, Box, Flex, Checkbox, Label, Container } from 'theme-ui'
import { useState, FC, Fragment, useCallback, useEffect, useMemo } from 'react'
import { sortByOptions } from '~/data/filters'
import FilterHeader from '~/components/filter/filter-header'
import FilterCategory from '~/components/filter/filter-category'
import FilterReset from '~/components/filter/filter-reset'
import FilterSelectedItems from '~/components/filter/filter-selected-items'
import useMediaQuery from '~/hooks/use-media-query'
import { FilterCategory as FilterCategoryType } from '~/@types/models'
import { space } from '~/gatsby-plugin-theme-ui'

type Props = {
  categories: FilterCategoryType[]
  onChange?: (type: string, value: string | boolean) => void
  onReset?: () => void
  toggleValue?: (type: string) => void
  selectedFilters: any
  setSelectedFilters: any
  infiniteScrollRef: any
  inStockOnlyBool: boolean
  setInStockOnlyBool: any
  resetFilters?: () => void
}
export enum EFilterName {
  filter = 'bsolFilters',
}

const Filter: FC<Props> = ({
  resetFilters,
  categories,
  selectedFilters = {},
  setSelectedFilters,
  infiniteScrollRef,
  inStockOnlyBool,
  setInStockOnlyBool,
}) => {
  const isMobile = useMediaQuery({
    query: breakpoints => `(max-device-width: ${breakpoints[2]})`,
  })
  const [showFilter, setShowFilter] = useState<boolean>(isMobile ? false : true)
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false)
  const [sortBy, setSortBy] = useState<string | null>(sortByOptions[0].value)

  const toggleValue = useCallback(
    (changeItem: { name: string; value: string }, categoryName: string) => {
      setSelectedFilters((state: any) => {
        const newState = { ...state }
        if (newState[categoryName]) {
          const index = newState[categoryName].filter(
            (item: { name: string; value: string }) => item.value === changeItem.value
          )
          if (index.length) {
            newState[categoryName] = newState[categoryName].filter(
              (item: { name: string; value: string }) => item.value !== changeItem.value
            )
          } else {
            newState[categoryName].push(changeItem)
          }
          if (!newState[categoryName].length) {
            delete newState[categoryName]
          }
        } else {
          newState[categoryName] = [changeItem]
        }
        return newState
      })

      if (infiniteScrollRef) {
        infiniteScrollRef.current.props.next()
      }
    },
    [setSelectedFilters, selectedFilters, infiniteScrollRef]
  )
  const handleResetFilters = useCallback(() => {
    setSelectedFilters({})
    setShowMoreFilters(false)
    if (resetFilters) {
      resetFilters()
    }
    localStorage.removeItem(EFilterName.filter)

    if (infiniteScrollRef) {
      infiniteScrollRef.current.props.next()
    }
  }, [setSelectedFilters, selectedFilters, infiniteScrollRef])

  const handleShowFilter = () => {
    setShowFilter(!showFilter)
  }

  const handleStockOnly = () => {
    setInStockOnlyBool(!inStockOnlyBool)
  }

  const handleSortBy = (value: string) => {
    setSortBy(value)
  }

  const selectedValuesArray = useMemo(() => {
    const selectedValues: string[] = []
    if (!selectedFilters) return selectedValues
    Object.keys(selectedFilters).forEach(key => {
      selectedFilters[key].forEach((value: string) => {
        selectedValues.push(value)
      })
    })
    return selectedValues
  }, [selectedFilters])

  return (
    <Box>
      {isMobile && (
        <FilterHeader
          handleShowFilter={handleShowFilter}
          showFilter={showFilter}
          handleStockOnly={handleStockOnly}
          handleSortBy={handleSortBy}
          sortByOptions={sortByOptions}
        />
      )}
      {showFilter && typeof document !== 'undefined' && (
        <Container sx={{ paddingLeft: 0 }}>
          <Box
            sx={{
              mt: 0,
              mb: [space.sm],
            }}
          >
            <Flex
              sx={{
                // paddingTop: !isMobile ? '25px' : 0,
                flexWrap: 'wrap',
                flexDirection: 'row',
                'div:last-child': {
                  marginLeft: isMobile ? null : 'auto',
                },
                div: {
                  marginLeft: isMobile ? null : '0px',
                },
                // 'div:first-child': {
                //   marginRight: isMobile ? null : '0px',
                // },
                justifyContent: 'center',
              }}
            >
              {categories.map(category => (
                <FilterCategory
                  key={category.name}
                  category={category}
                  selectedValues={selectedFilters[category.name]}
                  handleChange={toggleValue}
                  showMoreFilters={showMoreFilters}
                />
              ))}
              {!isMobile && <FilterReset handleClickResetFilters={handleResetFilters} />}
            </Flex>
          </Box>
          {isMobile && <FilterReset handleClickResetFilters={handleResetFilters} />}
        </Container>
      )}
      {selectedValuesArray && selectedValuesArray.length > 0 && (
        <FilterSelectedItems selectedFilters={selectedValuesArray} handleChange={toggleValue} />
      )}
    </Box>
  )
}

export default Filter
